import { useState, FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

type Role = { id: number; name: string }
type User = {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
  date_joined: string
  last_login: string | null
  roles: Role[]
}

type UserInput = {
  username: string
  email: string
  first_name: string
  last_name: string
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
  password?: string
  role_ids?: number[]
}

const emptyUser: UserInput = {
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  is_active: true,
  is_staff: true,
  is_superuser: false,
  password: '',
  role_ids: [],
}

export default function UsersList() {
  const qc = useQueryClient()

  const usersQ = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api.get<User[]>('/users/')).data,
  })

  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: async () => (await api.get('/roles/')).data,
  })

  const [editing, setEditing] = useState<User | null>(null)
  const [creating, setCreating] = useState(false)

  const createMut = useMutation({
    mutationFn: async (payload: UserInput) => (await api.post('/users/', payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setCreating(false)
    },
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: UserInput }) =>
      (await api.patch(`/users/${id}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setEditing(null)
    },
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/users/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const users: User[] = usersQ.data || []
  const roles: Role[] = rolesQ.data || []

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Users ({users.length})</h2>
        <Button onClick={() => setCreating(true)}>+ Add user</Button>
      </div>

      {usersQ.isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Username</Th>
                <Th>Email</Th>
                <Th>Name</Th>
                <Th>Roles</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <Td>
                    <span className="font-mono">{u.username}</span>
                    {u.is_superuser && (
                      <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                        super
                      </span>
                    )}
                  </Td>
                  <Td>{u.email || '—'}</Td>
                  <Td>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}</Td>
                  <Td>
                    {u.roles.length ? (
                      <div className="flex flex-wrap gap-1">
                        {u.roles.map((r) => (
                          <span
                            key={r.id}
                            className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded"
                          >
                            {r.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </Td>
                  <Td>
                    <span
                      className={`inline-block w-2 h-2 rounded-full mr-2 ${
                        u.is_active ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                    {u.is_active ? 'Active' : 'Inactive'}
                  </Td>
                  <Td>
                    <button
                      onClick={() => setEditing(u)}
                      className="text-blue-600 hover:underline text-sm mr-3"
                    >
                      Edit
                    </button>
                    {!u.is_superuser && (
                      <button
                        onClick={() => {
                          if (confirm(`Delete user ${u.username}?`)) deleteMut.mutate(u.id)
                        }}
                        className="text-red-600 hover:underline text-sm"
                      >
                        Delete
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <UserDialog
          initial={
            editing
              ? {
                  username: editing.username,
                  email: editing.email,
                  first_name: editing.first_name,
                  last_name: editing.last_name,
                  is_active: editing.is_active,
                  is_staff: editing.is_staff,
                  is_superuser: editing.is_superuser,
                  password: '',
                  role_ids: editing.roles.map((r) => r.id),
                }
              : emptyUser
          }
          isEdit={!!editing}
          allRoles={roles}
          submitting={createMut.isPending || updateMut.isPending}
          error={(createMut.error as any) || (updateMut.error as any)}
          onCancel={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSubmit={(payload) => {
            // Don't send empty password on edit
            const clean = { ...payload }
            if (editing && !clean.password) delete clean.password
            if (editing) updateMut.mutate({ id: editing.id, payload: clean })
            else createMut.mutate(clean)
          }}
        />
      )}
    </AdminLayout>
  )
}

function UserDialog({
  initial,
  isEdit,
  allRoles,
  onSubmit,
  onCancel,
  submitting,
  error,
}: {
  initial: UserInput
  isEdit: boolean
  allRoles: Role[]
  onSubmit: (payload: UserInput) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const [form, setForm] = useState<UserInput>(initial)
  // Username field is hidden by default during create — admin reveals it via
  // the "Use a different username" link. When editing, the username is shown
  // as read-only context.
  const [showUsernameOverride, setShowUsernameOverride] = useState(false)
  const set = <K extends keyof UserInput>(k: K, v: UserInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const toggleRole = (id: number) => {
    const ids = form.role_ids || []
    set('role_ids', ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id])
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit(form)
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{isEdit ? 'Edit user' : 'Add user'}</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="user-email">Email</FieldLabel>
                <Input
                  id="user-email"
                  type="email"
                  required={!isEdit && !form.username}
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  placeholder="user@example.com"
                />
                {!isEdit && (
                  <p className="text-xs text-gray-500 mt-1">
                    The username is set from the email by default.
                    {!showUsernameOverride && (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={() => setShowUsernameOverride(true)}
                          className="text-blue-600 hover:underline"
                        >
                          Use a different username
                        </button>
                      </>
                    )}
                  </p>
                )}
              </Field>
              {(isEdit || showUsernameOverride) && (
                <Field>
                  <FieldLabel htmlFor="user-username">Username</FieldLabel>
                  <Input
                    id="user-username"
                    type="text"
                    value={form.username}
                    onChange={(e) => set('username', e.target.value)}
                    placeholder="auto: derived from email"
                    disabled={isEdit}
                  />
                  {isEdit && (
                    <p className="text-xs text-gray-500 mt-1">Username can't be changed after creation.</p>
                  )}
                </Field>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="user-first-name">First name</FieldLabel>
                  <Input
                    id="user-first-name"
                    type="text"
                    value={form.first_name}
                    onChange={(e) => set('first_name', e.target.value)}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="user-last-name">Last name</FieldLabel>
                  <Input
                    id="user-last-name"
                    type="text"
                    value={form.last_name}
                    onChange={(e) => set('last_name', e.target.value)}
                  />
                </Field>
              </div>

              <Field>
                <FieldLabel htmlFor="user-password">
                  {isEdit ? 'New password (leave blank to keep current)' : 'Password'}
                </FieldLabel>
                <Input
                  id="user-password"
                  type="password"
                  required={!isEdit}
                  value={form.password || ''}
                  onChange={(e) => set('password', e.target.value)}
                  autoComplete="new-password"
                  minLength={isEdit ? undefined : 8}
                />
              </Field>

              <Field>
                <FieldLabel>Roles</FieldLabel>
                <div className="border border-gray-200 rounded-md p-2 space-y-1 max-h-40 overflow-y-auto">
                  {allRoles.length === 0 ? (
                    <p className="text-sm text-gray-400">No roles yet</p>
                  ) : (
                    allRoles.map((r) => (
                      <label key={r.id} className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={(form.role_ids || []).includes(r.id)}
                          onChange={() => toggleRole(r.id)}
                        />
                        <span className="text-sm text-gray-700">{r.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </Field>

              <div className="space-y-1 pt-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => set('is_active', e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={form.is_staff}
                    onChange={(e) => set('is_staff', e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">Staff (can access admin)</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={form.is_superuser}
                    onChange={(e) => set('is_superuser', e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">Superuser (bypasses permissions)</span>
                </label>
              </div>
            </FieldGroup>
          </FieldSet>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded mt-3">
              {String(
                error?.response?.data
                  ? JSON.stringify(error.response.data)
                  : error.message || error
              )}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{children}</th>
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm ${className || ''}`}>{children}</td>
}
